import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Box,
  TextField,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Grid,
  Button,
  Alert
} from '@mui/material';

import {
  Casino,
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet,
  Visibility,
  ArrowBack
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

interface Transaction {
  id: number;
  type: 'bet' | 'win' | 'deposit' | 'withdrawal';
  date: string;
  description: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'won' | 'lost';
  counter?: string;
  number?: string;
  betType?: 'big' | 'small' | 'both';
  referenceId?: number;
}



const transactionTypes = [
  { value: 'all', label: 'All Transactions' },
  { value: 'bet', label: 'Bets' },
  { value: 'win', label: 'Winnings' },
  { value: 'deposit', label: 'Deposits' },
  { value: 'withdrawal', label: 'Withdrawals' }
];

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' }
];

const UserTransactionHistory: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const userEmail = searchParams.get('userEmail');
  const userName = searchParams.get('userName');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['admin-user-history', userId, page, rowsPerPage, typeFilter, statusFilter, dateFrom, dateTo],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');
      
      // First get the user's wallet to get wallet ID
      const walletResponse = await axios.get(`/wallet/user/${userId}`);
      const wallet = walletResponse.data;
      
      if (!wallet) {
        throw new Error('User wallet not found');
      }
      
      // Then get transaction history using wallet ID
      const response = await axios.get(`/wallet/${wallet.id}/transactions`, {
        params: {
          page: page + 1,
          pageSize: rowsPerPage,
          txType: typeFilter === 'all' ? undefined : typeFilter,
          fromDate: dateFrom || undefined,
          toDate: dateTo || undefined
        }
      });
      
      const transactionData = response.data;
      
      // Transform the data to match our frontend interface
      const transformedTransactions = transactionData.transactions.map((tx: any) => ({
        id: tx.id,
        type: mapTxTypeToFrontendType(tx.txType),
        date: tx.createdAt,
        description: getTransactionDescription(tx),
        amount: tx.amount,
        status: 'completed', // Wallet transactions are typically completed
        referenceId: tx.referenceId || tx.id,
        note: tx.note
      }));
      
      // Calculate summary from transactions
      const summary = calculateTransactionSummary(transformedTransactions);
      
      return {
        transactions: transformedTransactions,
        totalCount: transactionData.totalCount,
        summary
      };
    },
    enabled: !!userId
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bet': return <Casino />;
      case 'win': return <TrendingUp />;
      case 'deposit': return <AccountBalanceWallet />;
      case 'withdrawal': return <TrendingDown />;
      default: return <Visibility />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'won': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  const getAmountColor = (amount: number) => {
    return amount >= 0 ? 'success.main' : 'error.main';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to map backend transaction types to frontend types
  const mapTxTypeToFrontendType = (txType: string) => {
    switch (txType?.toLowerCase()) {
      case 'deposit':
      case 'topup':
        return 'deposit';
      case 'withdrawal':
      case 'withdraw':
        return 'withdrawal';
      case 'bet':
      case 'betting':
        return 'bet';
      case 'win':
      case 'winning':
        return 'win';
      case 'adjustment':
      case 'manual_adjustment':
        return 'adjustment';
      default:
        return txType?.toLowerCase() || 'other';
    }
  };

  // Helper function to generate transaction description
  const getTransactionDescription = (tx: any) => {
    const txType = tx.txType?.toLowerCase();
    switch (txType) {
      case 'deposit':
      case 'topup':
        return 'Account top-up';
      case 'withdrawal':
      case 'withdraw':
        return 'Withdrawal request';
      case 'bet':
      case 'betting':
        return tx.note || 'Bet placed';
      case 'win':
      case 'winning':
        return tx.note || 'Winning payout';
      case 'adjustment':
      case 'manual_adjustment':
        return tx.note || 'Manual adjustment';
      default:
        return tx.note || tx.txType || 'Transaction';
    }
  };

  // Helper function to calculate transaction summary
  const calculateTransactionSummary = (transactions: any[]) => {
    const summary = {
      totalBets: 0,
      totalWinnings: 0,
      totalDeposits: 0,
      totalWithdrawals: 0,
      netProfit: 0
    };

    transactions.forEach(tx => {
      const amount = Math.abs(tx.amount);
      switch (tx.type) {
        case 'bet':
          summary.totalBets += amount;
          break;
        case 'win':
          summary.totalWinnings += amount;
          break;
        case 'deposit':
          summary.totalDeposits += amount;
          break;
        case 'withdrawal':
          summary.totalWithdrawals += amount;
          break;
      }
    });

    summary.netProfit = summary.totalWinnings + summary.totalDeposits - summary.totalBets - summary.totalWithdrawals;
    return summary;
  };

  if (!userId) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          User ID is required to view transaction history.
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/backoffice')}
          sx={{ mt: 2 }}
        >
          Back to Admin Panel
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/backoffice')}
          sx={{ mr: 2 }}
        >
          Back to Admin Panel
        </Button>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            User Transaction History
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {userName ? `${userName} (${userEmail})` : userEmail || `User ID: ${userId}`}
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      {historyData?.summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Bets
                </Typography>
                <Typography variant="h6">
                  {historyData.summary.totalBets}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
             <Card>
               <CardContent>
                 <Typography color="text.secondary" gutterBottom>
                   Total Winnings
                 </Typography>
                 <Typography variant="h6" color="success.main">
                   ${historyData.summary.totalWinnings.toFixed(2)}
                 </Typography>
               </CardContent>
             </Card>
           </Grid>
           <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
             <Card>
               <CardContent>
                 <Typography color="text.secondary" gutterBottom>
                   Total Deposits
                 </Typography>
                 <Typography variant="h6" color="primary.main">
                   ${historyData.summary.totalDeposits.toFixed(2)}
                 </Typography>
               </CardContent>
             </Card>
           </Grid>
           <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
             <Card>
               <CardContent>
                 <Typography color="text.secondary" gutterBottom>
                   Total Withdrawals
                 </Typography>
                 <Typography variant="h6" color="warning.main">
                   ${historyData.summary.totalWithdrawals.toFixed(2)}
                 </Typography>
               </CardContent>
             </Card>
           </Grid>
           <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Net Profit/Loss
                </Typography>
                <Typography 
                  variant="h6" 
                  color={historyData.summary.netProfit >= 0 ? 'success.main' : 'error.main'}
                >
                  ${historyData.summary.netProfit.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Transaction Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              size="small"
            >
              {transactionTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
             <TextField
               select
               fullWidth
               label="Status"
               value={statusFilter}
               onChange={(e) => setStatusFilter(e.target.value)}
               size="small"
             >
               {statusOptions.map((option) => (
                 <MenuItem key={option.value} value={option.value}>
                   {option.label}
                 </MenuItem>
               ))}
             </TextField>
           </Grid>
           <Grid size={{ xs: 12, sm: 6, md: 3 }}>
             <TextField
               type="date"
               fullWidth
               label="From Date"
               value={dateFrom}
               onChange={(e) => setDateFrom(e.target.value)}
               InputLabelProps={{ shrink: true }}
               size="small"
             />
           </Grid>
           <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              type="date"
              fullWidth
              label="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Transaction Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading transactions...
                  </TableCell>
                </TableRow>
              ) : (
                historyData?.transactions?.map((transaction: Transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={transaction.type}>
                          <IconButton size="small" color="primary">
                            {getTypeIcon(transaction.type)}
                          </IconButton>
                        </Tooltip>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {transaction.type}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transaction.date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.description}
                      </Typography>
                      {(transaction.counter || transaction.number) && (
                        <Box sx={{ mt: 0.5 }}>
                          {transaction.counter && (
                            <Chip 
                              label={transaction.counter} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mr: 0.5 }}
                            />
                          )}
                          {transaction.number && (
                            <Chip 
                              label={transaction.number} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mr: 0.5 }}
                            />
                          )}
                          {transaction.betType && (
                            <Chip 
                              label={transaction.betType} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        color={getAmountColor(transaction.amount)}
                      >
                        {transaction.amount >= 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.status}
                        color={getStatusColor(transaction.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {transaction.referenceId}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={historyData?.totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default UserTransactionHistory;